/**
 * Security Utilities
 * 
 * This module provides security-related utilities and helpers for the application.
 */

import DOMPurify from 'dompurify'

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param html - The HTML string to sanitize
 * @param options - Optional DOMPurify configuration
 * @returns Sanitized HTML string
 */
export const sanitizeHTML = (
  html: string,
  options?: DOMPurify.Config
): string => {
  const defaultConfig: DOMPurify.Config = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 
      'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'a', 'span', 'div'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    // Prevent data URIs and JavaScript protocols
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
  }

  return DOMPurify.sanitize(html, { ...defaultConfig, ...options })
}

/**
 * Validates and sanitizes user input
 * @param input - User input string
 * @returns Sanitized string with HTML entities escaped
 */
export const sanitizeUserInput = (input: string): string => {
  if (!input) return ''
  
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
}

/**
 * Content Security Policy configuration helper
 * Use this to configure CSP headers in your build/deployment
 */
export const CSP_CONFIG = {
  'default-src': ["'self'"],
  'script-src': [
    "'self'",
    "'unsafe-inline'", // Required for React inline scripts
    'https://kit.fontawesome.com',
    'https://ka-f.fontawesome.com'
  ],
  'style-src': [
    "'self'",
    "'unsafe-inline'", // Required for Material-UI
    'https://fonts.googleapis.com'
  ],
  'font-src': [
    "'self'",
    'https://fonts.gstatic.com',
    'https://ka-f.fontawesome.com'
  ],
  'img-src': [
    "'self'",
    'data:',
    'https:',
    'https://s3.eu-central-1.amazonaws.com',
    'https://stylink-ugc-production.s3.eu-central-1.amazonaws.com',
    'https://getnano-development.s3.eu-central-1.amazonaws.com',
    'https://duwhup56nt2mn.cloudfront.net',
    'https://d2hajobaa1dyhd.cloudfront.net'
  ],
  'connect-src': [
    "'self'",
    'https://mnkwl5qmzd.execute-api.eu-central-1.amazonaws.com',
    'https://cognito-idp.eu-central-1.amazonaws.com'
  ],
  'media-src': ["'self'", 'https:', 'blob:'],
  'frame-src': ["'self'"],
}

/**
 * Generates CSP header string from configuration
 */
export const generateCSPHeader = (): string => {
  return Object.entries(CSP_CONFIG)
    .map(([key, values]) => `${key} ${values.join(' ')}`)
    .join('; ')
}

/**
 * Token storage security utilities
 * 
 * SECURITY NOTE: This application currently uses localStorage for token storage.
 * While convenient, this approach has security implications:
 * 
 * VULNERABILITIES:
 * - Vulnerable to XSS attacks (any script can access localStorage)
 * - Tokens persist across sessions
 * - No httpOnly protection
 * 
 * MITIGATIONS IN PLACE:
 * - All user-generated content is sanitized using DOMPurify
 * - No eval() or Function() constructor usage
 * - Strict input validation
 * - CSP headers to prevent inline script injection
 * 
 * RECOMMENDED IMPROVEMENTS:
 * 1. Use httpOnly cookies for token storage (requires backend changes)
 * 2. Implement token refresh mechanism with short-lived access tokens
 * 3. Add CSRF protection for cookie-based auth
 * 4. Use Secure and SameSite cookie attributes
 * 5. Implement proper session management
 */

/**
 * Checks if the current environment is secure (HTTPS)
 */
export const isSecureContext = (): boolean => {
  return window.isSecureContext || window.location.protocol === 'https:'
}

/**
 * Validates that tokens are accessed in a secure context
 */
export const validateSecureTokenAccess = (): void => {
  if (process.env.NODE_ENV === 'production' && !isSecureContext()) {
    console.error(
      'SECURITY WARNING: Accessing tokens over insecure connection. ' +
      'Always use HTTPS in production.'
    )
  }
}

/**
 * Rate limiting helper for sensitive operations
 */
class RateLimiter {
  private attempts: Map<string, { count: number; resetAt: number }> = new Map()

  /**
   * Checks if an operation should be allowed
   * @param key - Unique identifier for the operation
   * @param maxAttempts - Maximum attempts allowed
   * @param windowMs - Time window in milliseconds
   * @returns true if operation is allowed
   */
  isAllowed(key: string, maxAttempts = 5, windowMs = 60000): boolean {
    const now = Date.now()
    const record = this.attempts.get(key)

    if (!record || now > record.resetAt) {
      this.attempts.set(key, { count: 1, resetAt: now + windowMs })
      return true
    }

    if (record.count >= maxAttempts) {
      return false
    }

    record.count++
    return true
  }

  /**
   * Resets attempts for a key
   */
  reset(key: string): void {
    this.attempts.delete(key)
  }
}

export const rateLimiter = new RateLimiter()

/**
 * Validates URL to prevent open redirects
 * @param url - URL to validate
 * @param allowedDomains - List of allowed domains
 * @returns true if URL is safe
 */
export const isUrlSafe = (url: string, allowedDomains: string[] = []): boolean => {
  try {
    const parsedUrl = new URL(url, window.location.origin)
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return false
    }

    // If no allowed domains specified, only allow same origin
    if (allowedDomains.length === 0) {
      return parsedUrl.origin === window.location.origin
    }

    // Check if domain is in allowed list
    return allowedDomains.some(domain => 
      parsedUrl.hostname === domain || 
      parsedUrl.hostname.endsWith(`.${domain}`)
    )
  } catch {
    return false
  }
}

